import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  BREAKPOINT, can, contactAttributes, formatIst, formatIstDate, inboxApi, PERMISSION, useAuth, useMediaQuery,
  type Contact, type ContactField, type Segment, type SegmentRule,
} from '@wa/shared';
import { Sheet } from '@/components/Sheet';
import { FieldsPanel } from '@/contacts/FieldsPanel';
import { ImportDialog } from '@/contacts/ImportDialog';
import { AddCustomerDialog } from '@/customers/AddCustomerDialog';
import { CustomerDetails, IconBolt, ListPicker } from '@/customers/CustomerDetails';
import { FilterBuilder, useAway } from '@/customers/FilterBuilder';
import { catalogue, complete, type FilterField } from '@/customers/filters';

/**
 * Customers, the reference's way: segments down the left, one table in the
 * middle with whichever columns you chose, a details panel on the right when a
 * row is open — and a filter builder above the table that is the same thing a
 * saved segment is made of, so "Create segment" is one click from a filter.
 *
 * The list is the server's: every filter, sort and page is a query, because a
 * tenant has forty thousand customers and a browser holding them all is a
 * browser that stops. Only the column choice is local.
 */
type Sort = { key: 'name' | 'phone' | 'created' | 'lastSeen'; dir: 'asc' | 'desc' } | null;

const FIXED = new Set(['name', 'phone']);

export function CustomersPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const wide = useMediaQuery(`(min-width: ${BREAKPOINT.md}px)`);
  const triple = useMediaQuery(`(min-width: ${BREAKPOINT.xl}px)`);

  const segmentId = Number(params.get('list')) || null;
  const selectedId = Number(id) || null;

  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => { const t = setTimeout(() => setQ(term.trim()), 250); return () => clearTimeout(t); }, [term]);

  // The filter being edited, and the one the list is showing: "Apply" moves the
  // first into the second, so typing a value does not fire a query per keystroke.
  const [draft, setDraft] = useState<SegmentRule[]>([]);
  const [applied, setApplied] = useState<SegmentRule[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sort, setSort] = useState<Sort>(null);
  const [columns, setColumns] = useState<Set<string>>(() => { try { return new Set(JSON.parse(localStorage.getItem('customers.columns') ?? '["name","phone"]') as string[]); } catch { return new Set(FIXED); } });
  useEffect(() => { try { localStorage.setItem('customers.columns', JSON.stringify([...columns])); } catch { /* fine */ } }, [columns]);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [segmentsOpen, setSegmentsOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [createSegment, setCreateSegment] = useState(false);
  const [addToList, setAddToList] = useState(false);
  const [erase, setErase] = useState<number[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fields = useQuery({ queryKey: ['contact-fields'], queryFn: inboxApi.contactFields });
  const segments = useQuery({ queryKey: ['segments'], queryFn: inboxApi.segments });
  const tags = useQuery({ queryKey: ['tags'], queryFn: inboxApi.tags });
  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels });
  const all = useMemo(() => catalogue(fields.data ?? []), [fields.data]);

  const segment = (segments.data ?? []).find((sg) => sg.id === segmentId) ?? null;

  // Opening a rule-based segment loads its rules into the builder — the
  // reference shows them, editable, above the segment's own list.
  useEffect(() => {
    setChecked(new Set());
    setApplied([]);
    if (segment?.kind === 1) {
      try { const parsed = JSON.parse(segment.rules ?? '[]') as SegmentRule[]; setDraft(parsed); setShowFilters(true); } catch { setDraft([]); }
    } else {
      setDraft([]); setShowFilters(false);
    }
  }, [segmentId, segment?.kind, segment?.rules]);

  const query = useMemo(() => {
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    if (segmentId) qs.set('segmentId', String(segmentId));
    if (applied.length) qs.set('rules', JSON.stringify(applied));
    return qs;
  }, [q, segmentId, applied]);

  const contacts = useInfiniteQuery({
    queryKey: ['contacts', query.toString(), sort],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams(query);
      if (sort) { qs.set('sort', sort.key); qs.set('dir', sort.dir); }
      if (pageParam) qs.set('cursor', pageParam);
      return inboxApi.contacts(qs);
    },
    getNextPageParam: (last) => last.nextCursor,
  });
  const count = useQuery({ queryKey: ['contacts-count', query.toString()], queryFn: () => inboxApi.contactsCount(query) });
  const items = useMemo(() => contacts.data?.pages.flatMap((p) => p.items) ?? [], [contacts.data]);
  const selected = items.find((c) => c.id === selectedId) ?? null;
  const detailQuery = useQuery({ queryKey: ['contact', selectedId], queryFn: () => inboxApi.contact(selectedId!), enabled: selectedId !== null && !selected });
  const open = selected ?? detailQuery.data ?? null;

  // Infinite scroll: the reference loads as you reach the bottom.
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current; if (!el) return;
    const io = new IntersectionObserver((entries) => { if (entries[0]?.isIntersecting && contacts.hasNextPage && !contacts.isFetchingNextPage) void contacts.fetchNextPage(); }, { rootMargin: '200px' });
    io.observe(el); return () => io.disconnect();
  }, [contacts]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    void queryClient.invalidateQueries({ queryKey: ['contacts-count'] });
    void queryClient.invalidateQueries({ queryKey: ['contact', selectedId] });
    void queryClient.invalidateQueries({ queryKey: ['segments'] });
  };
  const fail = (e: unknown, fallback: string) => setError(e instanceof Error ? e.message : fallback);

  const saveRules = useMutation({
    mutationFn: () => inboxApi.updateSegment(segmentId!, { rules: draft }),
    onSuccess: () => { setError(null); refresh(); }, onError: (e) => fail(e, 'Could not save the segment.'),
  });
  const addMembers = useMutation({
    mutationFn: ({ listId, ids }: { listId: number; ids: number[] }) => inboxApi.addSegmentMembers(listId, ids),
    onSuccess: () => { setChecked(new Set()); setAddToList(false); refresh(); }, onError: (e) => fail(e, 'Could not add them to that list.'),
  });
  const eraseMany = useMutation({
    mutationFn: async (ids: number[]) => { for (const cid of ids) await inboxApi.eraseContact(cid); },
    onSuccess: (_, ids) => { setChecked(new Set()); setErase(null); if (ids.includes(selectedId ?? -1)) navigate(`/customers${segmentId ? `?list=${segmentId}` : ''}`); refresh(); },
    onError: (e) => { setErase(null); fail(e, 'Could not delete.'); },
  });

  async function exportCsv() {
    try {
      const { blob, fileName } = await inboxApi.exportContacts(segmentId);
      const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = fileName; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) { fail(e, 'Could not export.'); }
  }

  const pick = (cid: number | null) => navigate(`/customers${cid ? `/${cid}` : ''}${segmentId ? `?list=${segmentId}` : ''}`);
  const pickSegment = (sid: number | null) => { setParams(sid ? { list: String(sid) } : {}); navigate(`/customers${sid ? `?list=${sid}` : ''}`); setSegmentsOpen(false); };

  const canFilterSave = segment?.kind === 1 && can(user, PERMISSION.segmentsWrite);
  const draftOk = draft.length > 0 && complete(draft, all);
  const shownColumns = all.filter((f) => f.column && columns.has(f.key));
  const toggleSort = (key: NonNullable<Sort>['key']) =>
    setSort((cur) => (cur?.key === key ? (cur.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' }));

  const segmentsColumn = (
    <SegmentsColumn
      segments={segments.data ?? []} selectedId={segmentId} onSelect={pickSegment}
      onImport={() => setImporting(true)} onSample={() => void sample()} canWrite={can(user, PERMISSION.segmentsWrite)}
      onFields={can(user, PERMISSION.contactsWrite) ? () => setFieldsOpen(true) : undefined}
      onRenamed={refresh} onError={(e) => fail(e, 'Could not change that list.')}
    />
  );

  return (
    <div className={`grid h-full min-h-0 bg-(--color-paper) ${open && triple ? 'md:grid-cols-[16.25rem_minmax(0,1fr)_22.5rem]' : 'md:grid-cols-[16.25rem_minmax(0,1fr)]'}`}>
      {wide ? <div className="min-h-0 border-r border-(--color-line) bg-(--color-surface)">{segmentsColumn}</div>
        : <Sheet open={segmentsOpen} side="left" label="Customer segments" onClose={() => setSegmentsOpen(false)}>{segmentsColumn}</Sheet>}

      <section className="flex min-h-0 min-w-0 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-(--color-line) bg-(--color-surface) px-3 sm:px-4">
          {!wide && (
            <button type="button" onClick={() => setSegmentsOpen(true)} aria-label="Customer segments" className="tap grid size-9 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)"><IconMenu /></button>
          )}
          <h1 className="truncate text-lead font-semibold">{segment?.name ?? 'All customers'}</h1>
          <span className="rounded-(--radius-chip) bg-(--color-brand-wash) px-2 py-0.5 text-small font-semibold tabular-nums text-(--color-brand)">
            {count.data ? count.data.count.toLocaleString('en-IN') : '…'}
          </span>
          {can(user, PERMISSION.segmentsWrite) && !segment && (
            <button type="button" disabled={applied.length === 0} onClick={() => setCreateSegment(true)} aria-label="Create segment"
              className="tap ml-auto flex h-10 items-center gap-2 rounded-(--radius-field) bg-(--color-brand) px-3 text-body font-semibold text-(--color-brand-ink) shadow-(--shadow-pop) hover:bg-(--color-brand-strong) disabled:bg-(--color-sunken) disabled:text-(--color-faint) disabled:shadow-none sm:px-4">
              <IconSegment /> <span className="hidden sm:inline">Create segment</span>
            </button>
          )}
        </header>

        <div className="flex shrink-0 flex-wrap items-center gap-2 px-3 pt-3 sm:px-4">
          <label className="flex h-11 min-w-0 flex-1 basis-48 items-center gap-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-3 sm:max-w-60">
            <IconSearch />
            <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search Customers" aria-label="Search customers" className="min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-(--color-faint)" />
          </label>
          {segment?.kind !== 0 && (
            <button type="button" onClick={() => setShowFilters((x) => !x)} aria-expanded={showFilters}
              className={`tap flex h-11 items-center gap-1.5 rounded-(--radius-field) border px-3 text-body font-medium ${showFilters ? 'border-(--color-brand) bg-(--color-brand-wash) text-(--color-brand)' : 'border-(--color-line) bg-(--color-surface) text-(--color-ink-soft)'}`}>
              <IconFilter /> {showFilters ? 'Hide Filters' : 'Add Filters'}
            </button>
          )}
          {applied.length > 0 && !segment && (
            <button type="button" onClick={() => { setApplied([]); setDraft([]); }} className="tap text-body font-medium text-(--color-ink-soft) hover:text-(--color-ink)">Clear all</button>
          )}
          <span className="ml-auto flex items-center gap-2">
            {can(user, PERMISSION.contactsWrite) && (
              <button type="button" onClick={() => setAdding(true)} className="tap flex h-11 items-center gap-1.5 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-3 text-body font-medium text-(--color-ink-soft) hover:bg-(--color-sunken)"><IconPlus /> <span className="hidden sm:inline">Add customer</span></button>
            )}
            {can(user, PERMISSION.contactsExport) && (
              <button type="button" onClick={() => void exportCsv()} aria-label="Export" className="tap flex h-11 items-center gap-1.5 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-3 text-body font-medium text-(--color-ink-soft) hover:bg-(--color-sunken)"><IconExport /> <span className="hidden sm:inline">Export</span></button>
            )}
            <ChooseFields all={all} columns={columns} onChange={setColumns} onManage={can(user, PERMISSION.contactsWrite) ? () => setFieldsOpen(true) : undefined} />
          </span>
        </div>

        {showFilters && segment?.kind !== 0 && (
          <div className="shrink-0 px-3 pt-3 sm:px-4">
            <FilterBuilder
              rules={draft} onChange={setDraft} fields={all} tags={tags.data ?? []} channels={channels.data ?? []}
              canApply={draftOk && (segment ? canFilterSave : true)}
              applyLabel={segment ? 'Apply filters' : 'Apply filters'}
              onApply={() => { if (segment) saveRules.mutate(); else setApplied(draft); }}
              onClear={segment ? undefined : () => { setDraft([]); setApplied([]); }}
            />
            {segment && !canFilterSave && <p className="mt-1 text-micro text-(--color-muted)">You can read this segment's rules; changing them needs the lists permission.</p>}
          </div>
        )}

        {error && <p role="alert" className="shrink-0 px-3 pt-2 text-small text-(--color-bad) sm:px-4">{error}</p>}

        <div className="pane-scroll min-h-0 flex-1 overflow-auto p-3 sm:p-4">
          <div className="overflow-hidden rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface)">
            {checked.size > 0 ? (
              <div className="flex h-12 items-center gap-1 bg-(--color-brand) px-2 text-(--color-brand-ink)">
                <button type="button" onClick={() => setChecked(new Set())} aria-label="Clear selection" className="tap grid size-9 place-items-center rounded-full hover:bg-white/15"><svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" {...s} /></svg></button>
                <span className="mx-1 h-6 w-px bg-white/30" />
                <span className="grid h-6 min-w-6 place-items-center rounded bg-white px-1 text-small font-semibold text-(--color-brand)">{checked.size}</span>
                <span className="ml-1 text-body font-semibold">Selected Items</span>
                <span className="mx-2 h-6 w-px bg-white/30" />
                {can(user, PERMISSION.segmentsWrite) && (
                  <button type="button" onClick={() => setAddToList(true)} className="tap flex h-9 items-center gap-1.5 rounded-(--radius-field) px-2 text-body font-semibold hover:bg-white/15"><IconListAdd /> Add to list</button>
                )}
                {can(user, PERMISSION.settingsWrite) && (
                  <button type="button" onClick={() => setErase([...checked])} className="tap flex h-9 items-center gap-1.5 rounded-(--radius-field) px-2 text-body font-semibold hover:bg-white/15"><IconTrash /> Delete</button>
                )}
              </div>
            ) : null}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left whitespace-nowrap">
                <thead>
                  <tr className={`h-11 text-small text-(--color-ink-soft) ${checked.size > 0 ? '' : 'bg-(--color-paper)'}`}>
                    <th className="w-10 pl-3">
                      <input type="checkbox" aria-label="Select all on this page" checked={items.length > 0 && checked.size === items.length}
                        onChange={(e) => setChecked(e.target.checked ? new Set(items.map((c) => c.id)) : new Set())} className="size-4.5 accent-(--color-brand)" />
                    </th>
                    {shownColumns.map((f) => (
                      <Th key={f.key} sortable={sortKey(f)} sorted={sort?.key === sortKey(f) ? sort.dir : null} onSort={() => { const k = sortKey(f); if (k) toggleSort(k); }}>{f.label}</Th>
                    ))}
                    <th className="w-12" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} onClick={() => pick(c.id)} className={`h-11 cursor-pointer border-t border-(--color-line-dim) hover:bg-(--color-sunken) ${c.id === selectedId ? 'bg-(--color-brand-wash)' : ''}`}>
                      <td className="pl-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Select ${c.displayName ?? c.waId}`} checked={checked.has(c.id)}
                          onChange={(e) => setChecked((prev) => { const n = new Set(prev); if (e.target.checked) n.add(c.id); else n.delete(c.id); return n; })} className="size-4.5 accent-(--color-brand)" />
                      </td>
                      {shownColumns.map((f) => <td key={f.key} className="max-w-64 truncate px-3 text-body">{cell(c, f, fields.data ?? [])}</td>)}
                      <td className="pr-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <RowMenu contact={c} canErase={can(user, PERMISSION.settingsWrite)} onOpenChat={() => void openChat(c)} onErase={() => setErase([c.id])} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {contacts.isPending && <p className="py-10 text-center text-small text-(--color-muted)">Loading…</p>}
            {contacts.isError && <p className="py-10 text-center text-small text-(--color-bad)">The customers could not be loaded.</p>}
            {contacts.isSuccess && items.length === 0 && (
              <div className="grid min-h-[40vh] place-items-center text-center">
                <div>
                  <IconWarnBig />
                  <p className="mt-2 text-body text-(--color-ink-soft)">{q || applied.length || segment ? 'No matching customers' : 'No customers yet'}</p>
                  {!q && !applied.length && !segment && (
                    <p className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1 text-body font-medium text-(--color-brand)">
                      {can(user, PERMISSION.contactsWrite) && <button type="button" onClick={() => setAdding(true)} className="hover:underline">Add a customer</button>}
                      {can(user, PERMISSION.contactsImport) && <button type="button" onClick={() => setImporting(true)} className="hover:underline">Import from a spreadsheet</button>}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div ref={sentinel} />
            {contacts.isFetchingNextPage && <p className="py-3 text-center text-small text-(--color-muted)">Loading more…</p>}
          </div>
        </div>
      </section>

      {open && (triple
        ? <div className="min-h-0 border-l border-(--color-line)"><CustomerDetails contact={open} fields={fields.data ?? []} segments={segments.data ?? []} onClose={() => pick(null)} onChanged={refresh} /></div>
        : <Sheet open side="right" label="Customer details" onClose={() => pick(null)}><CustomerDetails contact={open} fields={fields.data ?? []} segments={segments.data ?? []} onClose={() => pick(null)} onChanged={refresh} /></Sheet>)}

      {adding && (
        <AddCustomerDialog fields={fields.data ?? []} segments={segments.data ?? []} canPickLists={can(user, PERMISSION.segmentsWrite)}
          onClose={() => setAdding(false)} onCreated={(cid) => { setAdding(false); refresh(); pick(cid); }} />
      )}
      {importing && (
        <ImportDialog fields={fields.data ?? []} segments={segments.data ?? []} defaultSegmentId={segment?.kind === 0 ? segment.id : null}
          onClose={() => setImporting(false)} onDone={refresh} />
      )}
      <Sheet open={fieldsOpen} side="right" label="Custom fields" onClose={() => setFieldsOpen(false)}>
        <FieldsPanel fields={fields.data ?? []} onClose={() => setFieldsOpen(false)} />
      </Sheet>
      {createSegment && (
        <NameDialog title="Create segment" hint="A rule-based list: whoever matches these filters, whenever it is used." onClose={() => setCreateSegment(false)}
          onSave={async (name) => { const created = await inboxApi.createSegment({ name, kind: 1, rules: applied }); setCreateSegment(false); void queryClient.invalidateQueries({ queryKey: ['segments'] }); pickSegment(created.id); }} />
      )}
      {addToList && (
        <ListPicker title="Add to a list" segments={(segments.data ?? []).filter((sg) => sg.kind === 0)} on={new Set()} busy={addMembers.isPending}
          onToggle={(listId, on) => { if (on) addMembers.mutate({ listId, ids: [...checked] }); }} onClose={() => setAddToList(false)} />
      )}
      {erase && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setErase(null)}>
          <div role="alertdialog" aria-label="Delete customers" onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-(--radius-panel) bg-(--color-surface) p-4 shadow-(--shadow-pop)">
            <h3 className="text-lead font-semibold">Delete {erase.length === 1 ? 'this customer' : `${erase.length} customers`}?</h3>
            <p className="mt-1.5 text-body text-(--color-muted)">
              This erases the person: their contact, every conversation and message with them, and any attachments. It is what a "delete my data" request requires, and it cannot be undone. The ledger keeps the charges, without a name.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setErase(null)} className="tap rounded-(--radius-field) px-3 py-2 text-body font-medium text-(--color-muted) hover:bg-(--color-sunken)">Cancel</button>
              <button type="button" disabled={eraseMany.isPending} onClick={() => eraseMany.mutate(erase)} className="tap rounded-(--radius-field) bg-(--color-bad) px-4 py-2 text-body font-semibold text-white hover:opacity-90 disabled:opacity-60">{eraseMany.isPending ? 'Deleting…' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  async function openChat(c: Contact) {
    const channel = (channels.data ?? []).find((ch) => ch.isActive);
    if (!channel) { setError('No active WhatsApp number to open a chat from.'); return; }
    try {
      const r = await inboxApi.startChat(c.waId, channel.id);
      navigate(`/chats?open=${r.conversationId}`);
    } catch (e) { fail(e, 'Could not open a chat.'); }
  }
}

// The API builds it: this account's custom fields as columns, phone as text.
async function sample() {
  const { blob, fileName } = await inboxApi.importSample();
  const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = fileName || 'customers-sample.xlsx'; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function sortKey(f: FilterField): NonNullable<Sort>['key'] | null {
  return f.key === 'name' ? 'name' : f.key === 'phone' ? 'phone' : f.key === 'created' ? 'created' : f.key === 'last_inbound' ? 'lastSeen' : null;
}

function cell(c: Contact, f: FilterField, fields: ContactField[]): React.ReactNode {
  switch (f.key) {
    case 'name': return <span className="font-medium">{c.displayName ?? <span className="text-(--color-muted)">{c.waId}</span>}</span>;
    case 'phone': return `+${c.waId}`;
    case 'tags': return c.tagNames ?? '-';
    case 'opted_out': return c.optedOutAt ? 'True' : 'False';
    case 'created': return formatIst(c.createdAt);
    case 'last_inbound': return c.lastSeenAt ? formatIst(c.lastSeenAt) : '-';
    default: {
      const v = contactAttributes(c)[f.field ?? ''] ?? '';
      const def = fields.find((x) => x.fieldKey === f.field);
      if (v === '') return '-';
      if (def?.fieldType === 'boolean') return v === 'true' ? 'True' : 'False';
      if (def?.fieldType === 'date') return formatIstDate(v, v);
      return v;
    }
  }
}

function Th({ children, sortable, sorted, onSort }: { children: React.ReactNode; sortable: string | null; sorted: 'asc' | 'desc' | null; onSort: () => void }) {
  const inner = (
    <span className="inline-flex items-center gap-1">
      {children}
      {sortable && (
        <svg viewBox="0 0 20 20" className={`size-3.5 ${sorted ? 'text-(--color-ink)' : 'text-(--color-faint)'}`} aria-hidden="true">
          {sorted === null && <path d="m7 8.5 3-3 3 3M7 11.5l3 3 3-3" {...s} />}
          {sorted === 'asc' && <path d="m6.5 12 3.5-4 3.5 4" {...s} />}
          {sorted === 'desc' && <path d="m6.5 8 3.5 4 3.5-4" {...s} />}
        </svg>
      )}
    </span>
  );
  return (
    <th scope="col" aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined} className="border-l border-(--color-line-dim) px-3 font-medium first:border-l-0">
      {sortable ? <button type="button" onClick={onSort} className="tap text-inherit">{inner}</button> : inner}
    </th>
  );
}

/** The reference's "View Options → Choose fields". Name and phone are always on. */
function ChooseFields({ all, columns, onChange, onManage }: { all: FilterField[]; columns: Set<string>; onChange: (c: Set<string>) => void; onManage?: () => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef<HTMLDivElement>(null);
  useAway(box, open, () => setOpen(false));
  const shown = all.filter((f) => f.column && f.label.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div ref={box} className="relative">
      <button type="button" onClick={() => { setOpen((o) => !o); setQ(''); }} aria-expanded={open} aria-label="View Options" className={`tap flex h-11 items-center gap-1.5 rounded-(--radius-field) border bg-(--color-surface) px-3 text-body font-medium text-(--color-ink-soft) ${open ? 'border-(--color-brand)' : 'border-(--color-line)'}`}>
        <IconEye /> <span className="hidden sm:inline">View Options</span>
      </button>
      {open && (
        <div className="absolute top-full right-0 z-30 mt-1 w-80 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) p-3 shadow-(--shadow-pop)">
          <h3 className="text-lead font-semibold">Choose fields</h3>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fields" aria-label="Search fields" className="tap mt-2 h-9 w-full rounded-(--radius-field) border border-(--color-line) px-3 text-body outline-none focus:border-(--color-brand) placeholder:text-(--color-faint)" />
          <ul className="mt-2 max-h-72 overflow-y-auto">
            {shown.map((f) => {
              const fixed = FIXED.has(f.key);
              const on = columns.has(f.key);
              return (
                <li key={f.key}>
                  <label className={`flex items-center gap-2.5 py-1.5 ${fixed ? 'opacity-60' : 'cursor-pointer'}`}>
                    <input type="checkbox" checked={on} disabled={fixed} onChange={(e) => { const n = new Set(columns); if (e.target.checked) n.add(f.key); else n.delete(f.key); onChange(n); }} className="size-4.5 accent-(--color-brand)" />
                    <span className="text-body">{f.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
          {onManage && <button type="button" onClick={() => { setOpen(false); onManage(); }} className="tap mt-2 w-full border-t border-(--color-line-dim) pt-2 text-left text-body font-medium text-(--color-brand) hover:underline">Manage custom fields</button>}
        </div>
      )}
    </div>
  );
}

function RowMenu({ contact, canErase, onOpenChat, onErase }: { contact: Contact; canErase: boolean; onOpenChat: () => void; onErase: () => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLUListElement>(null);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  useAway(box, open, () => setOpen(false));

  // The menu is portalled to the body and placed from the button's rectangle.
  // Absolutely positioned inside the table it was clipped by the panel's
  // overflow: with three customers the list was shorter than the menu, and the
  // Delete item — the second one — sat below the panel's edge, invisible.
  useLayoutEffect(() => {
    if (!open || !box.current) { setAt(null); return; }
    const place = () => {
      const r = box.current?.getBoundingClientRect();
      if (r) setAt({ top: r.bottom + 4, right: window.innerWidth - r.right });
    };
    place();
    window.addEventListener('resize', place);
    document.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); document.removeEventListener('scroll', place, true); };
  }, [open]);

  // The click-outside guard keys off `box`; the portalled list lives elsewhere in
  // the DOM, so clicks inside it must not count as outside.
  useEffect(() => {
    if (!open) return;
    const keep = (e: MouseEvent) => { if (menu.current?.contains(e.target as Node)) e.stopPropagation(); };
    document.addEventListener('mousedown', keep, true);
    return () => document.removeEventListener('mousedown', keep, true);
  }, [open]);

  return (
    <div ref={box} className="relative inline-block">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-label={`Actions for ${contact.displayName ?? contact.waId}`} aria-expanded={open} className="tap grid size-9 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-line-dim)">
        <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><circle cx="10" cy="4.5" r="1.3" fill="currentColor" /><circle cx="10" cy="10" r="1.3" fill="currentColor" /><circle cx="10" cy="15.5" r="1.3" fill="currentColor" /></svg>
      </button>
      {open && at && createPortal(
        <ul ref={menu} role="menu" style={{ top: at.top, right: at.right }} className="fixed z-50 w-40 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) py-1 text-left shadow-(--shadow-pop)">
          <li><button type="button" role="menuitem" onClick={() => { setOpen(false); onOpenChat(); }} className="tap flex w-full items-center gap-2 px-3 py-2 text-body hover:bg-(--color-sunken)"><IconChat /> Open chat</button></li>
          {canErase && <li><button type="button" role="menuitem" onClick={() => { setOpen(false); onErase(); }} className="tap flex w-full items-center gap-2 px-3 py-2 text-body text-(--color-bad) hover:bg-(--color-sunken)"><IconTrash /> Delete</button></li>}
        </ul>,
        document.body,
      )}
    </div>
  );
}

function SegmentsColumn({ segments, selectedId, onSelect, onImport, onSample, onFields, canWrite, onRenamed, onError }: {
  segments: Segment[]; selectedId: number | null; onSelect: (id: number | null) => void; onImport: () => void; onSample: () => void; onFields?: () => void;
  canWrite: boolean; onRenamed: () => void; onError: (e: unknown) => void;
}) {
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const shown = segments.filter((sg) => sg.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex h-14 shrink-0 items-center gap-2 px-4">
        {searching
          ? <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search segments" aria-label="Search segments" className="min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-(--color-faint)" />
          : <h2 className="flex-1 truncate text-lead font-semibold">Customer segments</h2>}
        <button type="button" onClick={() => { setSearching((x) => !x); setQ(''); }} aria-label="Search segments" className="tap grid size-9 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)"><IconSearch /></button>
      </header>
      <div className={`mx-3 mb-2 grid ${onFields ? 'grid-cols-3' : 'grid-cols-2'} divide-x divide-(--color-line) rounded-(--radius-field) border border-(--color-line)`}>
        <button type="button" onClick={onImport} className="tap flex flex-col items-center gap-1 py-3 text-small font-medium text-(--color-ink-soft) hover:bg-(--color-sunken)"><IconExcel /> Excel Import</button>
        <button type="button" onClick={onSample} className="tap flex flex-col items-center gap-1 py-3 text-small font-medium text-(--color-ink-soft) hover:bg-(--color-sunken)"><IconFileDown /> Sample File</button>
        {onFields && <button type="button" onClick={onFields} className="tap flex flex-col items-center gap-1 py-3 text-small font-medium text-(--color-ink-soft) hover:bg-(--color-sunken)"><IconFields /> Custom Fields</button>}
      </div>
      <ul className="pane-scroll min-h-0 flex-1 overflow-y-auto">
        {!q && (
          <li>
            <button type="button" onClick={() => onSelect(null)} className={`tap flex w-full items-center gap-1.5 px-4 py-2.5 text-left text-body ${selectedId === null ? 'bg-(--color-brand-wash) font-semibold text-(--color-brand)' : 'text-(--color-ink) hover:bg-(--color-sunken)'}`}>
              All customers <IconBolt />
            </button>
          </li>
        )}
        {shown.map((sg) => <SegmentRow key={sg.id} segment={sg} active={sg.id === selectedId} onSelect={() => onSelect(sg.id)} canWrite={canWrite} onChanged={onRenamed} onError={onError} onDeleted={() => { if (sg.id === selectedId) onSelect(null); onRenamed(); }} />)}
        {shown.length === 0 && q && <li className="px-4 py-6 text-center text-small text-(--color-muted)">Nothing matches.</li>}
      </ul>
    </div>
  );
}

function SegmentRow({ segment, active, onSelect, canWrite, onChanged, onError, onDeleted }: {
  segment: Segment; active: boolean; onSelect: () => void; canWrite: boolean; onChanged: () => void; onError: (e: unknown) => void; onDeleted: () => void;
}) {
  const [menu, setMenu] = useState(false);
  const [rename, setRename] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useAway(box, menu, () => setMenu(false));
  const remove = useMutation({ mutationFn: () => inboxApi.deleteSegment(segment.id), onSuccess: () => { setConfirm(false); onDeleted(); }, onError: (e) => { setConfirm(false); onError(e); } });
  return (
    <li className="group relative">
      <button type="button" onClick={onSelect} className={`tap flex w-full items-center gap-1.5 px-4 py-2.5 pr-11 text-left text-body ${active ? 'bg-(--color-brand-wash) font-semibold text-(--color-brand)' : 'text-(--color-ink) hover:bg-(--color-sunken)'}`}>
        <span className="truncate">{segment.name}</span>{segment.kind === 1 && <IconBolt />}
      </button>
      {canWrite && (
        <div ref={box} className="absolute top-1/2 right-2 -translate-y-1/2">
          <button type="button" onClick={() => setMenu((m) => !m)} aria-label={`Actions for ${segment.name}`} className={`tap grid size-8 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-line-dim) ${menu ? '' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'}`}>
            <svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><circle cx="10" cy="4.5" r="1.3" fill="currentColor" /><circle cx="10" cy="10" r="1.3" fill="currentColor" /><circle cx="10" cy="15.5" r="1.3" fill="currentColor" /></svg>
          </button>
          {menu && (
            <ul role="menu" className="absolute top-full right-0 z-30 mt-1 w-36 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-pop)">
              <li><button type="button" role="menuitem" onClick={() => { setMenu(false); setRename(true); }} className="tap block w-full px-3 py-2 text-left text-body hover:bg-(--color-sunken)">Rename</button></li>
              <li><button type="button" role="menuitem" onClick={() => { setMenu(false); setConfirm(true); }} className="tap block w-full px-3 py-2 text-left text-body text-(--color-bad) hover:bg-(--color-sunken)">Delete</button></li>
            </ul>
          )}
        </div>
      )}
      {rename && <NameDialog title="Rename segment" initial={segment.name} onClose={() => setRename(false)} onSave={async (name) => { await inboxApi.updateSegment(segment.id, { name }); setRename(false); onChanged(); }} />}
      {confirm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setConfirm(false)}>
          <div role="alertdialog" aria-label="Delete segment" onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-(--radius-panel) bg-(--color-surface) p-4 shadow-(--shadow-pop)">
            <h3 className="text-lead font-semibold">Delete {segment.name}?</h3>
            <p className="mt-1.5 text-body text-(--color-muted)">The list goes; the customers on it stay. Broadcasts already sent to it keep their history.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirm(false)} className="tap rounded-(--radius-field) px-3 py-2 text-body font-medium text-(--color-muted) hover:bg-(--color-sunken)">Cancel</button>
              <button type="button" disabled={remove.isPending} onClick={() => remove.mutate()} className="tap rounded-(--radius-field) bg-(--color-bad) px-4 py-2 text-body font-semibold text-white disabled:opacity-60">Delete</button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

function NameDialog({ title, hint, initial = '', onClose, onSave }: { title: string; hint?: string; initial?: string; onClose: () => void; onSave: (name: string) => Promise<void> }) {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <form role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()} onSubmit={async (e) => { e.preventDefault(); if (!name.trim()) return; setBusy(true); try { await onSave(name.trim()); } catch (err) { setError(err instanceof Error ? err.message : 'Could not save.'); } finally { setBusy(false); } }}
        className="w-full max-w-sm rounded-(--radius-panel) bg-(--color-surface) p-4 shadow-(--shadow-pop)">
        <h3 className="text-lead font-semibold">{title}</h3>
        {hint && <p className="mt-1 text-small text-(--color-muted)">{hint}</p>}
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Segment name" aria-label="Segment name" className="tap mt-3 w-full rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-body outline-none focus:border-(--color-brand)" />
        {error && <p role="alert" className="mt-2 text-small text-(--color-bad)">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="tap rounded-(--radius-field) px-3 py-2 text-body font-medium text-(--color-muted) hover:bg-(--color-sunken)">Cancel</button>
          <button type="submit" disabled={busy || !name.trim()} className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong) disabled:opacity-60">Save</button>
        </div>
      </form>
    </div>
  );
}

const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IconSearch = () => <svg viewBox="0 0 20 20" className="size-4.5 shrink-0 text-(--color-muted)" aria-hidden="true"><circle cx="8.8" cy="8.8" r="5.2" {...s} /><path d="m12.8 12.8 3.6 3.6" {...s} /></svg>;
const IconFilter = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M3 5h14l-5.5 6.5V16l-3-1.5V11.5z" {...s} /></svg>;
const IconExport = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M10 12V3M6.5 6.5 10 3l3.5 3.5M4 12v4h12v-4" {...s} /></svg>;
const IconEye = () => <svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><path d="M2.5 10s2.8-5 7.5-5 7.5 5 7.5 5-2.8 5-7.5 5-7.5-5-7.5-5Z" {...s} /><circle cx="10" cy="10" r="2.3" {...s} /></svg>;
const IconSegment = () => <svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><rect x="3.5" y="3.5" width="13" height="13" rx="2" {...s} /><path d="M10 7v6M7 10h6" {...s} /></svg>;
const IconMenu = () => <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="M4 6h12M4 10h12M4 14h12" {...s} /></svg>;
const IconTrash = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M4 6h12M8 6V4h4v2M6 6l.8 10h6.4L14 6" {...s} /></svg>;
const IconListAdd = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M3 6h9M3 10h9M3 14h6M15 10v6M12 13h6" {...s} /></svg>;
const IconChat = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M4 5.5h12v8H8.5L5 16.5v-3H4z" {...s} /></svg>;
const IconExcel = () => <svg viewBox="0 0 24 24" className="size-6" aria-hidden="true"><path d="M8 4h9l4 4v12H8z" fill="#fff" stroke="#B9B9B9" strokeWidth="1.4" /><path d="M17 4v4h4" fill="none" stroke="#B9B9B9" strokeWidth="1.4" /><rect x="3" y="8" width="11" height="11" rx="1.5" fill="#1D6F42" /><path d="m6 11 5 5M11 11l-5 5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>;
const IconFileDown = () => <svg viewBox="0 0 24 24" className="size-6 text-(--color-ink-soft)" aria-hidden="true"><path d="M7 3h8l4 4v14H7z" {...s} /><path d="M15 3v4h4M12 10v7M9.5 14.5 12 17l2.5-2.5" {...s} /></svg>;
const IconPlus = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M10 4v12M4 10h12" {...s} /></svg>;
const IconFields = () => <svg viewBox="0 0 24 24" className="size-6 text-(--color-ink-soft)" aria-hidden="true"><rect x="4" y="5" width="16" height="4.5" rx="1.2" {...s} /><rect x="4" y="12.5" width="16" height="4.5" rx="1.2" {...s} /><path d="M7 7.25h4M7 14.75h6" {...s} /></svg>;
const IconWarnBig = () => <svg viewBox="0 0 24 24" className="mx-auto size-8 text-(--color-ink-soft)" aria-hidden="true"><path d="M12 3 22 20H2z" fill="currentColor" /><path d="M12 9v5M12 16.5v.3" stroke="var(--color-surface)" strokeWidth="1.8" strokeLinecap="round" /></svg>;
